import {
  Flex,
  Button,
  AcceptIcon,
  ExclamationTriangleIcon,
  ErrorIcon,
  WindowMinimizeIcon,
} from "@fluentui/react-northstar";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { DeviceStatus } from "../../../results-view/ResultsView.types";
import { Devices } from "../../../../../types/Device.types";

import { useMobileDeviceStatusDialogButtonStyles } from "./MobileDeviceStatusDialogButton.styles";
import { MobileDeviceStatusDialogButtonProps } from "./MobileDeviceStatusDialogButton.types";
export const MobileDeviceStatusDialogButton: React.FC<
  MobileDeviceStatusDialogButtonProps
> = ({ status, deviceName, isInfoFetched, openDeviceDialog }) => {
  const dialogButtonStyles = useMobileDeviceStatusDialogButtonStyles();
  const { t } = useTranslation();

  const deviceNameString = useMemo(() => {
    switch (deviceName) {
      case Devices.Network:
        return t("NETWORK_TITLECASE");
      case Devices.Camera:
        return t("CAMERA_TITLECASE");
      case Devices.Speaker:
        return t("SPEAKER_TITLECASE");
      case Devices.Mic:
        return t("MIC_TITLECASE");
    }
  }, [deviceName, t]);

  const deviceStatusString = useMemo(() => {
    switch (status) {
      case DeviceStatus.WORKING:
        return t("STATUS_WORKING");
      case DeviceStatus.FAILED:
        return t("STATUS_FAILED");
      case DeviceStatus.MISSING:
        return t("STATUS_MISSING");
      case DeviceStatus.UNAVAILABLE:
        return t("STATUS_UNAVAILABLE");
      default:
        return "";
    }
  }, [status, t]);

  const deviceWorkingAriaLabel = useMemo(() => {
    switch (deviceName) {
      case Devices.Network:
        return t("NETWORK_CONNECTED_A11Y_LABEL");
      case Devices.Camera:
        return t("CAMERA_WORKING_A11Y_LABEL");
      case Devices.Speaker:
        return t("SPEAKER_WORKING_A11Y_LABEL");
      case Devices.Mic:
        return t("MICROPHONE_WORKING_A11Y_LABEL");
    }
  }, [deviceName, t]);

  const chooseDialogIcon = useMemo(() => {
    if (status === DeviceStatus.FAILED || status === DeviceStatus.MISSING) {
      return <ErrorIcon size="medium" />;
    } else if (status === DeviceStatus.UNAVAILABLE) {
      return (
        <ExclamationTriangleIcon
          size="medium"
          styles={dialogButtonStyles.warningIcon}
        />
      );
    } else if (status === DeviceStatus.WORKING) {
      return (
        <Flex vAlign="center" hAlign="center">
          <AcceptIcon
            aria-label={deviceWorkingAriaLabel}
            bordered
            circular
            size="smaller"
            styles={dialogButtonStyles.successIcon}
          />
        </Flex>
      );
    } else {
      return (
        <Flex vAlign="center" hAlign="center">
          <WindowMinimizeIcon size="smaller" />
        </Flex>
      );
    }
  }, [
    status,
    dialogButtonStyles.warningIcon,
    dialogButtonStyles.successIcon,
    deviceWorkingAriaLabel,
  ]);

  return (
    <Flex vAlign="center" hAlign="center">
      <Button
        className="focus-button"
        data-testid="openDialogBox"
        aria-label={t("OPEN_MODAL_PROMPT_A11Y_LABEL", {
          device: deviceNameString,
          status: deviceStatusString,
        })}
        onClick={openDeviceDialog}
        styles={dialogButtonStyles.dialogBoxTrigger}
        inverted
        disabled={!isInfoFetched}
        iconOnly
        icon={chooseDialogIcon}
      />
    </Flex>
  );
};
